import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingOverlay from '../../components/loading/loading';
import authService from '../../services/authService';
import Logo from '../../images/logoEMT.png';

const NavLink = memo(({ to, children, isActive, onClick }) => (
    <li className="nav-item">
        <Link
            to={to}
            onClick={onClick}
            className={`nav-link px-3 ${isActive ? 'active fw-semibold text-success' : ''}`}
        >
            {children}
        </Link>
    </li>
));

const Header = ({ hClass }) => {
    const [loading, setLoading] = useState(false);
    const [balance, setBalance] = useState(0);
    const [name, setName] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(
        sessionStorage.getItem('isLoggedIn') === 'true'
    );
    const [isNavCollapsed, setIsNavCollapsed] = useState(true);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        document.body.style.paddingTop = '76px';
        return () => {
            document.body.style.paddingTop = '0';
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getUserDetailsByID = useCallback(async () => {
        if (!isLoggedIn) return;

        try {
            setLoading(true);
            const userDetail = await authService.getUserDetailsById(
                sessionStorage.getItem('userId')
            );
            if (userDetail?.success) {
                setName(userDetail.user.firstName);
                setBalance(userDetail.balance.balance);
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }, [isLoggedIn]);

    useEffect(() => {
        getUserDetailsByID();
    }, [getUserDetailsByID]);

    const handleLogout = async () => {
        try {
            setLoading(true);
            const result = await authService.logout();
            if (result.success) {
                toast.success(result.message);
                sessionStorage.setItem('isLoggedIn', 'false');
                setIsLoggedIn(false);
                window.location.href = '/';
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const isActive = useCallback((path) => location.pathname === path, [location]);

    const mainNavItems = [
        { path: '/home', label: 'Home' },
        ...(isLoggedIn
            ? [
                { path: '/bookings/history', label: 'Bookings' },
                { path: '/wallet/transactions', label: 'Wallet' },
                { path: '/bank/details', label: 'Withdrawal' },
            ]
            : []),
        { path: '/rules', label: 'Help Center' },
        { path: '/events-offers', label: 'Events' },
        { path: '/contact', label: 'Contact Us' },
        { path: '/about', label: 'About Us' },
    ];

    return (
        <header className="fixed-top">
            {loading && <LoadingOverlay />}

            <nav className={`navbar navbar-expand-lg navbar-light bg-white shadow-sm ${hClass}`}>
                <div className="container-fluid px-4">
                    <Link to="/home" className="navbar-brand d-flex align-items-center">
                        <img src={Logo} alt="Logo" height="40" className="d-inline-block align-text-top" />
                    </Link>

                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={() => setIsNavCollapsed(!isNavCollapsed)}
                        aria-controls="navbarContent"
                        aria-expanded={!isNavCollapsed}
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>

                    <div className={`collapse navbar-collapse ${isNavCollapsed ? '' : 'show'}`} id="navbarContent">
                        <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                            {mainNavItems.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    isActive={isActive(item.path)}
                                    onClick={() => {
                                        setIsNavCollapsed(true);
                                        window.scrollTo(0, 0);
                                    }}
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            
                            {/* Mobile-only menu items */}
                            {isLoggedIn && (
                                <div className="d-lg-none">
                                    <li className="nav-item">
                                        <span className="nav-link px-3 text-success">
                                            <i className="fa fa-money me-2"></i>₹ {balance.toFixed(2)}
                                        </span>
                                    </li>
                                    <NavLink
                                        to="/profile"
                                        isActive={isActive('/profile')}
                                        onClick={() => {
                                            setIsNavCollapsed(true);
                                            window.scrollTo(0, 0);
                                        }}
                                    >
                                        <i className="fa fa-user me-2"></i>Profile
                                    </NavLink>
                                    <li className="nav-item">
                                        <Link
                                            to=""
                                            className="nav-link px-3 text-danger"
                                            onClick={() => {
                                                setIsNavCollapsed(true);
                                                handleLogout();
                                            }}
                                        >
                                            <i className="fa fa-sign-out-alt me-2"></i>Logout
                                        </Link>
                                    </li>
                                </div>
                            )}
                        </ul>

                        {/* Desktop-only auth section */}
                        <div className="d-none d-lg-flex align-items-center gap-3">
                            {isLoggedIn ? (
                                <div className="dropdown" ref={dropdownRef}>
                                    <button
                                        className={`btn btn-outline-secondary dropdown-toggle ${isDropdownOpen ? 'show' : ''}`}
                                        type="button"
                                        onClick={toggleDropdown}
                                        aria-expanded={isDropdownOpen}
                                    >
                                        <i className="fa fa-user me-1"></i> {name}
                                    </button>
                                    <ul
                                        className={`dropdown-menu dropdown-menu-end ${isDropdownOpen ? 'show' : ''}`}
                                        style={{
                                            display: isDropdownOpen ? 'block' : 'none',
                                            position: 'absolute',
                                            transform: 'translate3d(0px, 40px, 0px)',
                                            top: '0',
                                            right: '0',
                                            willChange: 'transform'
                                        }}
                                    >
                                        <li>
                                            <span className="dropdown-item" style={{ color: 'green' }}>
                                                <i className="fa fa-money me-2"></i>₹ {balance.toFixed(2)}
                                            </span>
                                        </li>
                                        <li><hr className="dropdown-divider" /></li>
                                        <li>
                                            <Link
                                                to="/profile"
                                                className="dropdown-item"
                                                onClick={() => setIsDropdownOpen(false)}
                                            >
                                                <i className="fa fa-user me-2"></i>Profile
                                            </Link>
                                        </li>
                                        <li><hr className="dropdown-divider" /></li>
                                        <li>
                                            <button
                                                className="dropdown-item text-danger"
                                                onClick={() => {
                                                    setIsDropdownOpen(false);
                                                    handleLogout();
                                                }}
                                            >
                                                <i className="fa fa-sign-out-alt me-2"></i>Logout
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            ) : (
                                <div className="d-flex gap-2">
                                    <Link to="/login" className="btn btn-outline-primary">
                                        <i className="fa fa-sign-in-alt me-1"></i>Login
                                    </Link>
                                    <Link to="/signup" className="btn btn-primary">
                                        <i className="fa fa-user-plus me-1"></i>Sign Up
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Mobile-only login/signup buttons */}
                        {!isLoggedIn && (
                            <div className="d-lg-none d-flex flex-column gap-2 mt-3">
                                <Link to="/login" className="btn btn-outline-primary">
                                    <i className="fa fa-sign-in-alt me-1"></i>Login
                                </Link>
                                <Link to="/signup" className="btn btn-primary">
                                    <i className="fa fa-user-plus me-1"></i>Sign Up
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </nav>
        </header>
    );
};

export default memo(Header);